import React, { useEffect, useState } from "react";
import Page from "../../Components/Page";
import { clsx } from "clsx";
import {
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight,
  IconChevronLeft,
} from "@tabler/icons-react";
import { getAlumniRegData } from "../../controllers/alumniRegistration.controller";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { TfiDownload } from "react-icons/tfi";
import { FiCheck, FiX } from "react-icons/fi"; // Import icons
import { updateApprovalStatus } from "../../controllers/alumniRegistration.controller"; // Import update function

const AlumniRegistration = () => {
  const [data, setData] = useState({
    tableData: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
    },
  });

  const { tableData, pagination } = data;
  const { currentPage, totalPages, totalItems } = pagination;

  // State for filtering
  const [nameFilter, setNameFilter] = useState("");
  const [approvalStatusFilter, setApprovalStatusFilter] = useState("");

  // Local state to hold filter values before submission
  const [localNameFilter, setLocalNameFilter] = useState("");
  const [localApprovalStatusFilter, setLocalApprovalStatusFilter] = useState("");


  // Fetch data with pagination and filtering
  const fetchTableData = async (page = 1, name = "", approvalStatus = "") => {
    try {
      let queryParams = `?page=${page}`;
      if (name) {
        queryParams += `&name=${name}`;
      }
      if (approvalStatus) {
        queryParams += `&approvalStatus=${approvalStatus}`;
      }

      const res = await getAlumniRegData(queryParams); // Pass queryParams to controller

      if (res.status === 200) {
        const apiData = res?.data?.data || [];
        const paginationData = res?.data?.pagination || {};

        setData({
          tableData: apiData.map((d) => ({
            id: d.id,
            reg_no: d.reg_no,
            name: d.name,
            permanent_address: d.permanent_address,
            correspondence_address: d.correspondence_address,
            mobile_no: d.mobile_no,
            resident_no: d.resident_no,
            personal_email: d.personal_email,
            office_email: d.office_email,
            nic_passport_no: d.nic_passport_no,
            designation: d.designation,
            official_address: d.official_address,
            sab_academic_qualifications: d.sab_academic_qualifications,
            year_of_enrollment: d.year_of_enrollment,
            sab_reg_no: d.sab_reg_no,
            intake: d.intake,
            year_of_degree_obtained: d.year_of_degree_obtained,
            approval_status: d.approval_status, // Include approval status
          })),
          pagination: {
            currentPage: paginationData.current_page || 1,
            totalPages: paginationData.total_pages || 1,
            totalItems: paginationData.total || 0,
          },
        });
      }
    } catch (error) {
      console.error("Error fetching alumni registration data:", error);
    }
  };

  useEffect(() => {
    fetchTableData(currentPage, nameFilter, approvalStatusFilter);
  }, [currentPage, nameFilter, approvalStatusFilter]); // Refetch when filters change

  // Pagination Handlers
  const btnPaginationFirstPage = () =>
    setData((ps) => ({
      ...ps,
      pagination: { ...ps.pagination, currentPage: 1 },
    }));
  const btnPaginationLastPage = () =>
    setData((ps) => ({
      ...ps,
      pagination: { ...ps.pagination, currentPage: totalPages },
    }));
  const btnPaginationNextPage = () => {
    if (currentPage < totalPages) {
      setData((ps) => ({
        ...ps,
        pagination: { ...ps.pagination, currentPage: currentPage + 1 },
      }));
    }
  };
  const btnPaginationPreviousPage = () => {
    if (currentPage > 1) {
      setData((ps) => ({
        ...ps,
        pagination: { ...ps.pagination, currentPage: currentPage - 1 },
      }));
    }
  };

  const generateRowPDF = (rowData) => {
    const doc = new jsPDF();

    // Add Title
    doc.setFontSize(14);
    doc.text("Alumni Registration Details", 14, 10);

    // Prepare table content
    const tableContent = [
      ["Field", "Value"],
      ["RID", rowData.reg_no],
      ["Name", rowData.name],
      ["Permanent Address", rowData.permanent_address],
      ["Correspondence Address", rowData.correspondence_address],
      ["Mobile Number", rowData.mobile_no],
      ["Resident Number", rowData.resident_no],
      ["Personal Email", rowData.personal_email],
      ["Office Email", rowData.office_email],
      ["NIC/Passport Number", rowData.nic_passport_no],
      ["Profession/Designation", rowData.designation],
      ["Official Address", rowData.official_address],
      ["Academic Qualification", rowData.sab_academic_qualifications],
      ["Year of Enrollment", rowData.year_of_enrollment],
      ["SAB Registration Number", rowData.sab_reg_no],
      ["Intake", rowData.intake],
      ["Year of Obtaining Degree", rowData.year_of_degree_obtained],
    ];

    doc.autoTable({
      head: [["Field", "Value"]],
      body: tableContent
        .slice(1)
        .map(([field, value]) => [field, value || "N/A"]),
      startY: 20,
      styles: {
        fontSize: 10,
        textColor: "#000000",
      },
      headStyles: {
        fillColor: "#92288C",
        textColor: "#FFFFFF",
      },
      columnStyles: {
        0: { textColor: "#92288C" },
        1: { textColor: "#92288C" },
      },
    });

    // Save the PDF
    doc.save(`Alumni Registration ${rowData.reg_no}.pdf`);
  };

  // Function to handle approval status update
  const handleUpdateApprovalStatus = async (id, approvalStatus) => {
    const confirmationMessage = `Are you sure you want to ${approvalStatus.toLowerCase()} this application?`;

    if (window.confirm(confirmationMessage)) {
      try {
        await updateApprovalStatus(id, { approvalStatus });
        // Refresh the table data after successful update
        fetchTableData(currentPage, nameFilter, approvalStatusFilter);
      } catch (error) {
        console.error("Error updating approval status:", error);
        alert("Failed to update approval status.");
      }
    }
  };

  const handleFilterSubmit = () => {
    setNameFilter(localNameFilter);
    setApprovalStatusFilter(localApprovalStatusFilter);
  };

  return (
    <Page className="px-8 py-6">
      <div className="mb-6 text-xl font-bold">Alumni Registration List</div>

      <div className="w-full p-6 mt-6 bg-white rounded-lg ">
        <h2 className="mb-4 text-xl font-semibold text-gray-700">
          Filter All Applications
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label
              className="block mb-1 text-sm font-medium text-gray-600"
              htmlFor="name"
            >
              Name
            </label>
            <input
              id="name"
              name="name"
              type="text"
              onChange={(e) => setLocalNameFilter(e.target.value)}
              value={localNameFilter}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
            />
          </div>

          <div>
            <label
              className="block mb-1 text-sm font-medium text-gray-600"
              htmlFor="approvalStatus"
            >
              Approval Status
            </label>
            <select
              id="approvalStatus"
              name="approvalStatus"
              value={localApprovalStatusFilter}
              onChange={(e) => setLocalApprovalStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
            >
              <option value="">Select Approval Status</option>
              <option value="Approved">Approved</option>
              <option value="Pending">Pending</option>
              <option value="Rejected">Reject</option>
            </select>
          </div>
          <div>
            <button
              className="px-4 py-2 mt-5 ml-8 font-bold border rounded text-primary-color border-primary-color bg-primary-color bg-opacity-10"
              onClick={handleFilterSubmit}
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      <div className="w-[1200px] h-[750px] overflow-auto mt-4">
        <table className="bg-white border-collapse rounded-lg shadow-md ">
          <thead>
            <tr className="text-left text-gray-500 border-b">
              <th className="px-4 py-2">RID</th>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Permanent Address</th>
              <th className="px-4 py-2">Correspondence Address</th>
              <th className="px-4 py-2">Mobile Number</th>
              <th className="px-4 py-2">Resident Number</th>
              <th className="px-4 py-2">Personal Email</th>
              <th className="px-4 py-2">Office Email</th>
              <th className="px-4 py-2">NIC/Passport Number</th>
              <th className="px-4 py-2">Profession/Designation</th>
              <th className="px-4 py-2">Company Name</th>
              <th className="px-4 py-2">BSc. (Applied Accounting)</th>
              <th className="px-4 py-2">Year of Enrollment</th>
              <th className="px-4 py-2">SAB Registration Number</th>
              <th className="px-4 py-2">Intake</th>
              <th className="px-4 py-2">Year of Obtaining Degree</th>
              <th className="px-4 py-2">Approval Status</th>
              <th className="px-4 py-2">Actions</th>
              <th className="px-4 py-2">Download application</th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((item) => (
              <tr key={item.id} className="border-b hover:bg-gray-50 ">
                <td className="px-4 py-2 text-center">{item.reg_no}</td>
                <td className="px-4 py-2">{item.name}</td>
                <td className="px-4 py-2">{item.permanent_address}</td>
                <td className="px-4 py-2">{item.correspondence_address}</td>
                <td className="px-4 py-2">{item.mobile_no}</td>
                <td className="px-4 py-2">{item.resident_no}</td>
                <td className="px-4 py-2">{item.personal_email}</td>
                <td className="px-4 py-2">{item.office_email}</td>
                <td className="px-4 py-2">{item.nic_passport_no}</td>
                <td className="px-4 py-2">{item.designation}</td>
                <td className="px-4 py-2">{item.official_address}</td>
                <td className="px-4 py-2">{item.sab_academic_qualifications}</td>
                <td className="px-4 py-2">{item.year_of_enrollment}</td>
                <td className="px-4 py-2">{item.sab_reg_no}</td>
                <td className="px-4 py-2">{item.intake}</td>
                <td className="px-4 py-2">{item.year_of_degree_obtained}</td>
                <td className="px-4 py-2">{item.approval_status}</td>
                <td className="px-4 py-2">
                  <div className="flex items-center space-x-2">
                  {item.approval_status !== "Approved" && (
                    <button
                      className="px-4 py-2 border rounded text-primary-color border-primary-color bg-primary-color bg-opacity-10"
                      onClick={() =>
                        handleUpdateApprovalStatus(item.id, "Approved")
                      }
                    >
                      Approve
                    </button>
                  )}
                  {item.approval_status !== "Rejected" && (
                    <button
                      className="flex items-center px-4 py-2 text-gray-400 bg-gray-100 border border-gray-400 rounded"
                      onClick={() =>
                        handleUpdateApprovalStatus(item.id, "Rejected")
                      }
                    >
                      Reject
                    </button>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2 text-center">
                  <button
                    className="btn btn-sm bg-[#92288C] text-white flex items-center justify-center w-[40px] h-[30px] rounded "
                    onClick={() => generateRowPDF(item)}
                  >
                    <TfiDownload />
                  </button>
                </td>
              </tr>
            ))}
            {tableData.length === 0 && (
              <tr>
                <td colSpan="17" className="py-4 text-center text-gray-500">
                  No data available.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* pagination */}
      <div className="flex justify-center mt-8 text-[15px] ">
        <div className="flex">
          <button
            onClick={btnPaginationFirstPage}
            className={clsx("join-item btn btn-sm bg-gray-100 text-black", {
              "btn-disabled": currentPage == 1,
            })}
          >
            <IconChevronsLeft stroke={1.5} />
          </button>
          <button
            onClick={btnPaginationPreviousPage}
            className={clsx("join-item btn btn-sm bg-gray-100 text-black", {
              "btn-disabled": currentPage == 1,
            })}
          >
            <IconChevronLeft stroke={1.5} />
          </button>
          <p className="text-black bg-gray-100 join-item">Page {currentPage}</p>
          <button
            onClick={btnPaginationNextPage}
            className={clsx("join-item btn btn-sm bg-gray-100 text-black", {
              "btn-disabled": currentPage == totalPages,
            })}
          >
            <IconChevronRight stroke={1.5} />
          </button>
          <button
            onClick={btnPaginationLastPage}
            className={clsx("join-item btn btn-sm bg-gray-100 text-black", {
              "btn-disabled": currentPage == totalPages,
            })}
          >
            <IconChevronsRight stroke={1.5} />
          </button>
        </div>
      </div>
      <div className="text-center mt-4 text-[15px]">
        <p>
          Showing {tableData.length} of {totalItems}
        </p>
      </div>
      {/* pagination */}
    </Page>
  );
};

export default AlumniRegistration;